import React, { useEffect, useState } from 'react';
import type { MembershipFigures } from '../types';
import { formatCount } from '../utils/membershipFormat';

/** Movements in the same order as the desktop columns. */
const MOVEMENTS: Array<{ key: keyof MembershipFigures; label: string; sign: '+' | '-' }> = [
  { key: 'added_count', label: 'Added', sign: '+' },
  { key: 'transferred_in_count', label: 'Transferred in', sign: '+' },
  { key: 'deceased_count', label: 'Deceased', sign: '-' },
  { key: 'transferred_out_count', label: 'Transferred out', sign: '-' },
  { key: 'excluded_count', label: 'Excluded', sign: '-' },
];

const PHONE_QUERY = '(max-width: 575.98px)';

/** True on phone widths, where the history and breakdown show rows instead of a table. */
export function usePhone(): boolean {
  const query = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(PHONE_QUERY)
    : null;
  const [phone, setPhone] = useState(Boolean(query?.matches));
  useEffect(() => {
    if (!query) return;
    const listener = (event: MediaQueryListEvent) => setPhone(event.matches);
    query.addEventListener?.('change', listener);
    return () => query.removeEventListener?.('change', listener);
  }, [query]);
  return phone;
}

function valueOf(row: MembershipFigures, key: keyof MembershipFigures): number {
  const value = row[key] as number | null | undefined;
  return value ?? 0;
}

interface Props {
  label: string;
  figures: MembershipFigures;
  /** Nested rows, for example the churches of a district. */
  children?: React.ReactNode;
  nested?: boolean;
}

/** One district or church row on phone. Collapsed by default; open shows male and female and the movements. */
const MembershipPhoneRow: React.FC<Props> = ({ label, figures, children, nested = false }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`membership-phone-row${nested ? ' membership-phone-row--nested' : ''}`}>
      <div className="membership-history-card__child">
        <button
          type="button"
          className="membership-caret membership-caret--row btn btn-link btn-sm p-0 text-start"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          <span className="membership-caret__mark" aria-hidden="true">{open ? '\u25BE' : '\u25B8'}</span>
          <span className="membership-history__name" title={label}>{label}</span>
        </button>
        <span>{formatCount(valueOf(figures, 'total_members'))}</span>
      </div>
      {open && (
        <>
          <div className="membership-phone-row__sub">
            Male {formatCount(valueOf(figures, 'male_count'))} {'\u00B7'} Female {formatCount(valueOf(figures, 'female_count'))}
          </div>
          <div className="membership-phone-row__moves">
            {MOVEMENTS.map((movement) => {
              const value = valueOf(figures, movement.key);
              return (
                <span key={movement.key} className={`membership-phone-row__move${value === 0 ? ' membership-zero' : ''}`}>
                  <span
                    className={`membership-phone-row__sign membership-phone-row__sign--${movement.sign === '+' ? 'in' : 'out'}`}
                    aria-hidden="true"
                  >
                    {movement.sign === '+' ? '+' : '\u2212'}
                  </span>
                  {movement.label} {formatCount(value)}
                </span>
              );
            })}
          </div>
          {children}
        </>
      )}
    </div>
  );
};

export default MembershipPhoneRow;
